import { format, isToday, parse } from 'date-fns'
import { ClipboardList, Settings2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { CLIENT_ID } from '@/api/client'
import { generateDailyAdvice, getBodyProfile, getDailySummary, listTemplates } from '@/api/services/nutrition'
import type { BodyProfile, DailyNutrition, FoodTemplate, MealLog, TargetBreakdown } from '@/api/types/nutrition'
import { BodyMetricsSummary } from '@/components/shared/body-metrics-summary'
import { DateNavigator } from '@/components/shared/date-navigator'
import { Header } from '@/components/shared/header'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import { Spinner } from '@/components/ui/spinner'
import { useGoBack } from '@/hooks/useGoBack'
import { useSyncEventListener } from '@/hooks/useSyncEventListener'
import { useMealPlanStore } from '@/store/useMealPlanStore'

import { AiAdviceCard } from './components/ai-advice-card'
import { BodyProfileDialog } from './components/body-profile-dialog'
import type { SheetInit } from './components/food-palette/palette-utils'
import { MealPlanSheet } from './components/meal-plan-sheet'
import { MealSheet } from './components/meal-sheet'
import { MealTable } from './components/meal-table'
import { NutritionTipsPopover } from './components/nutrition-tips-card'
import { QuickAddBar } from './components/quick-add-bar'

const EMPTY_MEALS: MealLog[] = []

function toDateStr(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function PfcBar({
  label,
  actual,
  target,
  color,
  unit,
}: {
  label: string
  actual: number
  target: number | null
  color: string
  unit: string
}) {
  const pct = target && target > 0 ? Math.min((actual / target) * 100, 100) : 0
  const diff = target != null ? actual - target : null

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span className={color}>{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {actual.toFixed(0)}
          {target != null && ` / ${target.toFixed(0)}`} {unit}
          {diff != null && diff !== 0 && (
            <span className={`ml-1 ${diff > 0 ? 'text-destructive' : 'text-amber-500'}`}>
              ({diff > 0 ? '+' : ''}
              {diff.toFixed(0)})
            </span>
          )}
        </span>
      </div>
      <Progress value={pct} className="h-2" />
    </div>
  )
}

/** たんぱく質バー: 体重×1.62 / ×2.0 / ×2.2 の区間マーカー付き */
function ProteinBar({ actual, weightKg }: { actual: number; weightKg: number }) {
  const low = weightKg * 1.62
  const mid = weightKg * 2.0
  const high = weightKg * 2.2
  const maxVal = high
  const fillPct = Math.min((actual / maxVal) * 100, 100)
  const lowPct = (low / maxVal) * 100
  const midPct = (mid / maxVal) * 100
  const highPct = (high / maxVal) * 100

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span className="text-blue-400">P (たんぱく質)</span>
        <span className="text-muted-foreground tabular-nums">
          {actual.toFixed(0)} / {low.toFixed(0)}〜{high.toFixed(0)} g
          {actual < low && <span className="ml-1 text-amber-500">({(actual - low).toFixed(0)})</span>}
          {actual > high && <span className="ml-1 text-destructive">(+{(actual - high).toFixed(0)})</span>}
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary/20">
        {/* 満点ゾーン背景 */}
        <div
          className="absolute inset-y-0 bg-emerald-500/15"
          style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%` }}
        />
        {/* 実績バー */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-all"
          style={{ width: `${fillPct}%` }}
        />
        {/* マーカー: 1.62x */}
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${lowPct}%` }} />
        {/* マーカー: 2.0x */}
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${midPct}%` }} />
        {/* マーカー: 2.2x */}
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${highPct}%` }} />
      </div>
      {/* マーカーラベル */}
      <div className="relative h-3 text-[9px] text-muted-foreground">
        <span className="absolute -translate-x-1/2" style={{ left: `${lowPct}%` }}>
          {low.toFixed(0)}
        </span>
        <span className="absolute -translate-x-1/2 text-emerald-400" style={{ left: `${midPct}%` }}>
          {mid.toFixed(0)}
        </span>
        <span className="absolute -translate-x-1/2" style={{ left: `${highPct}%` }}>
          {high.toFixed(0)}
        </span>
      </div>
    </div>
  )
}

/** 脂質バー: 目標±10gの範囲マーカー付き */
function FatBar({ actual, target }: { actual: number; target: number }) {
  const low = target - 10
  const high = target + 10
  const maxVal = high
  const fillPct = Math.min((actual / maxVal) * 100, 100)
  const lowPct = (low / maxVal) * 100
  const midPct = (target / maxVal) * 100
  const highPct = (high / maxVal) * 100

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span className="text-yellow-400">F (脂質)</span>
        <span className="text-muted-foreground tabular-nums">
          {actual.toFixed(0)} / {low.toFixed(0)}〜{high.toFixed(0)} g
          {actual < low && <span className="ml-1 text-amber-500">({(actual - low).toFixed(0)})</span>}
          {actual > high && <span className="ml-1 text-destructive">(+{(actual - high).toFixed(0)})</span>}
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary/20">
        <div
          className="absolute inset-y-0 bg-emerald-500/15"
          style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%` }}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-all"
          style={{ width: `${fillPct}%` }}
        />
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${lowPct}%` }} />
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${midPct}%` }} />
        <div className="absolute inset-y-0 w-px bg-muted-foreground/50" style={{ left: `${highPct}%` }} />
      </div>
      <div className="relative h-3 text-[9px] text-muted-foreground">
        <span className="absolute -translate-x-1/2" style={{ left: `${lowPct}%` }}>
          {low.toFixed(0)}
        </span>
        <span className="absolute -translate-x-1/2 text-emerald-400" style={{ left: `${midPct}%` }}>
          {target.toFixed(0)}
        </span>
        <span className="absolute -translate-x-1/2" style={{ left: `${highPct}%` }}>
          {high.toFixed(0)}
        </span>
      </div>
    </div>
  )
}

const GOAL_LABELS: Record<string, string> = {
  lean_bulk: 'リーンバルク',
  cut: '減量',
  maintain: '維持',
  auto_switch: '自動',
}

function formatGoalLabel(breakdown: { goal: string; effective_goal: string; goal_adjustment: number }): string {
  const adj = breakdown.goal_adjustment
  const sign = adj >= 0 ? '+' : ''
  const adjStr = `${sign}${Math.round(adj)}`
  if (breakdown.goal === 'auto_switch') {
    const effLabel = GOAL_LABELS[breakdown.effective_goal] ?? breakdown.effective_goal
    return `自動→${effLabel} (${adjStr})`
  }
  const label = GOAL_LABELS[breakdown.goal] ?? breakdown.goal
  return `${label} (${adjStr})`
}

function TargetCalorieBar({
  actual,
  target,
  breakdown,
}: {
  actual: number
  target: number | null
  breakdown: TargetBreakdown | null
}) {
  const pct = target && target > 0 ? Math.min((actual / target) * 100, 100) : 0

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span className="text-orange-400">
          目標カロリー
          {breakdown && (
            <span className="ml-1.5 text-muted-foreground font-normal">({formatGoalLabel(breakdown)})</span>
          )}
        </span>
        <span className="text-muted-foreground tabular-nums">
          {actual.toFixed(0)}
          {target != null && (
            <>
              {' / '}
              {breakdown ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="underline decoration-dotted underline-offset-2 text-orange-400">
                      {target.toFixed(0)}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 text-xs space-y-1.5 p-3" side="top">
                    <p className="font-medium mb-2">目標カロリー算出根拠</p>
                    <div className="space-y-1 text-muted-foreground">
                      <p>
                        基礎代謝(BMR): <span className="text-foreground">{breakdown.bmr.toFixed(0)} kcal</span>
                      </p>
                      <p>
                        活動係数: <span className="text-foreground">×{breakdown.activity_multiplier.toFixed(2)}</span>
                        {breakdown.avg_steps != null && (
                          <span className="ml-1">({breakdown.avg_steps.toLocaleString()}歩/日)</span>
                        )}
                      </p>
                      <p>
                        維持カロリー: <span className="text-foreground">{breakdown.maintenance.toFixed(0)} kcal</span>
                      </p>
                      <p>
                        目標調整: <span className="text-foreground">{formatGoalLabel(breakdown)}</span>
                      </p>
                      {breakdown.body_fat != null && (
                        <p>
                          体脂肪率: <span className="text-foreground">{breakdown.body_fat.toFixed(1)}%</span>
                        </p>
                      )}
                      {breakdown.cut_rate_pct != null && (
                        <p>
                          減量ペース: <span className="text-foreground">週{breakdown.cut_rate_pct.toFixed(1)}%</span>
                        </p>
                      )}
                      <p>
                        タンパク質: <span className="text-foreground">{breakdown.protein_g_per_kg}g/kg</span>
                      </p>
                      {breakdown.calorie_offset !== 0 && (
                        <>
                          <hr className="border-border my-1" />
                          <p className="font-medium text-foreground">
                            自動調整: {breakdown.calorie_offset > 0 ? '+' : ''}
                            {breakdown.calorie_offset} kcal
                          </p>
                          {breakdown.adjustment_detail && breakdown.adjustment_detail.evaluated_from && (
                            <div className="space-y-0.5 text-[10px]">
                              <p>
                                評価期間: {breakdown.adjustment_detail.evaluated_from} 〜{' '}
                                {breakdown.adjustment_detail.evaluated_to}
                              </p>
                              <p>
                                実摂取平均: {breakdown.adjustment_detail.actual_avg_intake.toFixed(0)} kcal/日 (
                                {breakdown.adjustment_detail.logged_days}日分)
                              </p>
                              <p>期待BF変化: {breakdown.adjustment_detail.expected_bf_delta.toFixed(3)}%/週</p>
                              <p>
                                実際BF変化: {breakdown.adjustment_detail.actual_bf_delta.toFixed(3)}%/週 (
                                {breakdown.adjustment_detail.data_points}点)
                              </p>
                              <p>達成率: {(breakdown.adjustment_detail.deviation_ratio * 100).toFixed(0)}%</p>
                            </div>
                          )}
                          {breakdown.adjustment_detail?.next_evaluation && (
                            <p className="text-[10px]">次回評価: {breakdown.adjustment_detail.next_evaluation}</p>
                          )}
                        </>
                      )}
                      {breakdown.calorie_offset === 0 && breakdown.adjustment_detail?.next_evaluation && (
                        <>
                          <hr className="border-border my-1" />
                          <p className="text-[10px]">
                            自動調整: 次回評価 {breakdown.adjustment_detail.next_evaluation}
                          </p>
                        </>
                      )}
                      <hr className="border-border my-1" />
                      <p className="text-[10px]">
                        {breakdown.weight_kg}kg / {breakdown.height_cm ?? '--'}cm / {breakdown.age ?? '--'}歳 /{' '}
                        {breakdown.gender === 'male' ? '男性' : '女性'}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                target.toFixed(0)
              )}
            </>
          )}{' '}
          kcal
          {target != null && actual !== 0 && (
            <span className={`ml-1 ${actual - target > 0 ? 'text-destructive' : 'text-amber-500'}`}>
              ({actual - target > 0 ? '+' : ''}
              {(actual - target).toFixed(0)})
            </span>
          )}
        </span>
      </div>
      <Progress value={pct} className="h-2" />
    </div>
  )
}

export const NutritionPage = () => {
  const goBack = useGoBack()
  const [searchParams, setSearchParams] = useSearchParams()

  const currentDate = useMemo(() => {
    const dateParam = searchParams.get('date')
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      return parse(dateParam, 'yyyy-MM-dd', new Date())
    }
    return new Date()
  }, [searchParams])

  const setCurrentDate = useCallback(
    (d: Date) => {
      setSearchParams({ date: format(d, 'yyyy-MM-dd') }, { replace: true })
    },
    [setSearchParams],
  )

  const [daily, setDaily] = useState<DailyNutrition | null>(null)
  const [loading, setLoading] = useState(true)
  const [adviceLoading, setAdviceLoading] = useState(false)
  const [bodyProfileOpen, setBodyProfileOpen] = useState(false)
  const [profileIncomplete, setProfileIncomplete] = useState(false)
  const [eufyCustomerId, setEufyCustomerId] = useState<string | null>(null)
  const [addMealOpen, setAddMealOpen] = useState(false)
  // 記録バーから食事シートを開くときのタブ・AI 入力欄・DB の検索文字
  const [sheetInit, setSheetInit] = useState<SheetInit | null>(null)
  const [mealPlanOpen, setMealPlanOpen] = useState(false)
  const [templates, setTemplates] = useState<FoodTemplate[]>([])

  const fetchPlan = useMealPlanStore((s) => s.fetchPlan)

  const dateStr = toDateStr(currentDate)

  /** プロフィールが必須項目を満たしているか確認 */
  const checkProfile = useCallback(async () => {
    try {
      const p: BodyProfile = await getBodyProfile()
      setEufyCustomerId(p.eufy_customer_id)
      if (!p.weight_kg || !p.height_cm || !p.age) {
        setProfileIncomplete(true)
        setBodyProfileOpen(true)
      } else {
        setProfileIncomplete(false)
      }
    } catch {
      // 取得失敗時は一旦スキップ
    }
  }, [])

  const reloadTemplates = useCallback(async () => {
    try {
      const data = await listTemplates()
      setTemplates(data)
    } catch {
      // テンプレート読み込みエラーは無視
    }
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getDailySummary(dateStr)
      setDaily(data)
    } catch {
      toast.error('データ取得に失敗')
    } finally {
      setLoading(false)
    }
  }, [dateStr])

  // ワンタップ記録用: スピナーを出さずにデータだけ差し替える（バーの状態とスクロール位置を保つ）
  const refreshQuietly = useCallback(async () => {
    try {
      setDaily(await getDailySummary(dateStr))
    } catch {
      toast.error('データ取得に失敗')
    }
  }, [dateStr])

  useEffect(() => {
    void checkProfile()
    void reload()
    void reloadTemplates()
    fetchPlan(dateStr).catch(() => {})
  }, [checkProfile, reload, reloadTemplates, fetchPlan, dateStr])

  // 他端末での変更を SSE で受信して自動反映
  useSyncEventListener(
    useCallback(
      (event) => {
        if ('origin' in event && event.origin === CLIENT_ID) return
        switch (event.type) {
          case 'nutrition.meal.created':
          case 'nutrition.meal.updated':
          case 'nutrition.meal.deleted':
            void reload()
            break
          case 'nutrition.profile.updated':
            void checkProfile()
            break
          case 'nutrition.template.created':
          case 'nutrition.template.updated':
          case 'nutrition.template.deleted':
            void reloadTemplates()
            break
          default:
            break
        }
      },
      [reload, checkProfile, reloadTemplates],
    ),
  )

  const handleAdvice = async () => {
    setAdviceLoading(true)
    try {
      const data = await generateDailyAdvice(dateStr)
      setDaily(data)
      toast.success('アドバイスを生成しました')
    } catch {
      toast.error('アドバイス生成に失敗')
    } finally {
      setAdviceLoading(false)
    }
  }

  return (
    <div className="flex h-dvh flex-col bg-background overflow-x-hidden">
      <Header
        title={<DateNavigator date={currentDate} onDateChange={setCurrentDate} />}
        onClose={goBack}
        dropdownSections={[
          ...(isToday(currentDate)
            ? [
                {
                  type: 'action' as const,
                  label: '食事',
                  items: [
                    {
                      key: 'meal-plan',
                      label: '食事の計画',
                      icon: <ClipboardList className="size-4" />,
                      onClick: async () => {
                        setDaily(await getDailySummary(dateStr))
                        setMealPlanOpen(true)
                      },
                    },
                  ],
                },
              ]
            : []),
          {
            type: 'action',
            label: '設定',
            items: [
              {
                key: 'body-profile',
                label: '身体プロフィール',
                icon: <Settings2 className="size-4" />,
                onClick: () => setBodyProfileOpen(true),
              },
            ],
          },
        ]}
      />
      <BodyProfileDialog
        open={bodyProfileOpen}
        onOpenChange={(v) => {
          if (!v && profileIncomplete) return
          setBodyProfileOpen(v)
        }}
        onSaved={() => {
          setProfileIncomplete(false)
          setBodyProfileOpen(false)
          checkProfile()
          reload()
        }}
        required={profileIncomplete}
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="space-y-4 p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : daily ? (
            <>
              {/* カロリーバランス */}
              <div className="space-y-3 gap-1">
                {/* 消費内訳 + ヒントボタン */}

                {daily.burned_total == null && daily.steps != null && (
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <span>{daily.steps.toLocaleString()} 歩</span>
                    {daily.target_breakdown && (
                      <NutritionTipsPopover
                        weightKg={daily.target_breakdown.weight_kg}
                        breakdown={daily.target_breakdown}
                      />
                    )}
                  </div>
                )}

                {daily.burned_total == null && daily.steps == null && daily.target_breakdown && (
                  <div className="flex justify-center">
                    <NutritionTipsPopover
                      weightKg={daily.target_breakdown.weight_kg}
                      breakdown={daily.target_breakdown}
                    />
                  </div>
                )}

                {/* 目標バー */}
                <TargetCalorieBar
                  actual={daily.total_calories}
                  target={daily.target_calories}
                  breakdown={daily.target_breakdown}
                />
                {daily.burned_total != null && (
                  <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
                    {daily.burned_bmr != null && <span>基礎代謝 {Math.round(daily.burned_bmr)}</span>}
                    {daily.burned_active != null && <span>活動 {Math.round(daily.burned_active)}</span>}
                    {daily.steps != null && <span>{daily.steps.toLocaleString()} 歩</span>}
                    {daily.target_breakdown && (
                      <NutritionTipsPopover
                        weightKg={daily.target_breakdown.weight_kg}
                        breakdown={daily.target_breakdown}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* PFCサマリー */}
              <div className="space-y-3">
                {daily.target_breakdown?.goal === 'lean_bulk' && daily.target_breakdown?.weight_kg ? (
                  <ProteinBar actual={daily.total_protein_g} weightKg={daily.target_breakdown.weight_kg} />
                ) : (
                  <PfcBar
                    label="P (たんぱく質)"
                    actual={daily.total_protein_g}
                    target={daily.target_protein_g}
                    color="text-blue-400"
                    unit="g"
                  />
                )}
                {daily.target_breakdown?.goal === 'lean_bulk' && daily.target_fat_g ? (
                  <FatBar actual={daily.total_fat_g} target={daily.target_fat_g} />
                ) : (
                  <PfcBar
                    label="F (脂質)"
                    actual={daily.total_fat_g}
                    target={daily.target_fat_g}
                    color="text-yellow-400"
                    unit="g"
                  />
                )}
                <PfcBar
                  label="C (炭水化物)"
                  actual={daily.total_carb_g}
                  target={daily.target_carb_g}
                  color="text-green-400"
                  unit="g"
                />
              </div>
              {/* AIアドバイス */}
              <AiAdviceCard daily={daily} adviceLoading={adviceLoading} onGenerate={handleAdvice} />

              {/* 食事一覧（記録は画面下の入力欄から） */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">食事記録</h3>
                <MealTable
                  meals={daily.meals}
                  templates={templates}
                  onChanged={reload}
                  onTemplateCreated={reloadTemplates}
                />
              </div>

              <MealSheet
                open={addMealOpen}
                onOpenChange={setAddMealOpen}
                date={dateStr}
                meals={daily?.meals ?? EMPTY_MEALS}
                templates={templates}
                onSaved={reload}
                onTemplateCreated={reloadTemplates}
                initial={sheetInit}
                remainingCalories={daily.target_calories != null ? daily.target_calories - daily.total_calories : null}
              />

              <MealPlanSheet
                open={mealPlanOpen}
                onOpenChange={setMealPlanOpen}
                todayDate={dateStr}
                meals={daily?.meals ?? []}
                templates={templates}
                targetCalories={daily?.target_calories ?? null}
                targetProteinG={daily?.target_protein_g ?? null}
                targetFatG={daily?.target_fat_g ?? null}
                targetCarbG={daily?.target_carb_g ?? null}
              />

              {/* 体組成サマリ (Eufy) */}
              {eufyCustomerId && <BodyMetricsSummary customerId={eufyCustomerId} date={dateStr} />}
            </>
          ) : null}
        </div>
      </div>

      {/* 下部固定: 食事を記録する入口（読み直し中も消さない） */}
      {/* スマホで画面の下端・ホームインジケータに近すぎて押しにくいので、safe-area の上にさらに余白を足す */}
      {daily && (
        <div className="border-t border-border bg-background pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <QuickAddBar
            // 日付を切り替えた直後は daily がまだ前の日なので、表示中のデータの日付に合わせる
            key={daily.date}
            date={daily.date}
            isToday={daily.date === toDateStr(new Date())}
            meals={daily.meals}
            templates={templates}
            onRecorded={refreshQuietly}
            onOpenSheet={(init) => {
              setSheetInit(init)
              setAddMealOpen(true)
            }}
          />
        </div>
      )}
    </div>
  )
}
